import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  actionsColumn,
  hideColumn,
  textColumn,
  tooltipColumn
} from "../../components/tableComponent/columns";
import TableComponent from "../../components/tableComponent";
import {
  callApiAsync,
  getBillingUnits,
  getBusinessUnits,
  getBillingPeriodsByBusinessUnit,
  getPackage,
  getPricingInstance,
  closeBillingAndCreateNextPeriod,
  deletePricingInstance,
  createInitialBillingPeriod
} from "../../services/apiService";
import ToastNotification from "../../components/toastNotification";
import ButtonComponent from "../../components/button";
import icons from "../../constants/icons";
import SearchableSelectInput from "../../components/selectInput/searchableSelectInput";
import { transformToSelectData } from "../../utils/selectsValues";
import { useForm } from "react-hook-form";
import AddPackageToBillModal from "./addPackageToBillModal";
import AlertComponent from "../../components/alertModal";
import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import ModalComponent from "../../components/modalComponent";

const MONTH_NAMES_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre"
];

/** Mes/año en UTC para alinear con el texto del modal y el payload al API. */
function getInitialPeriodTargetUtc(choice) {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  if (choice === "current") return { year: y, month: m };
  if (m === 1) return { year: y - 1, month: 12 };
  return { year: y, month: m - 1 };
}

const PricingInstanceFacturacion = () => {
  const [billingUnits, setBillingUnits] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [periodInstances, setPeriodInstances] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [editingInstanceId, setEditingInstanceId] = useState(null);
  const [closeBillingAlertOpen, setCloseBillingAlertOpen] = useState(false);
  const [closingBilling, setClosingBilling] = useState(false);
  const [deleteInstanceAlertOpen, setDeleteInstanceAlertOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState(null);

  const [toastMessage, setToastMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("warning");

  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [periodsLoadError, setPeriodsLoadError] = useState(null);
  const [openInitialPeriodModal, setOpenInitialPeriodModal] = useState(false);
  const [initialPeriodChoice, setInitialPeriodChoice] = useState("previous");
  const [creatingInitialPeriod, setCreatingInitialPeriod] = useState(false);

  const periodsFetchSeqRef = useRef(0);
  const pendingSelectBillingPeriodRef = useRef(null);

  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      idBillingUnit: "",
      idBusinessUnit: "",
      idBillingPeriod: ""
    }
  });

  const watchBillingUnit = watch("idBillingUnit");
  const watchBusinessUnit = watch("idBusinessUnit");
  const watchBillingPeriod = watch("idBillingPeriod");

  const loadBillingUnits = () => {
    callApiAsync(getBillingUnits, (data) => {
      const selectData = transformToSelectData(data || []);
      setBillingUnits(selectData);
    });
  };

  const loadBusinessUnits = () => {
    callApiAsync(getBusinessUnits, (data) => {
      setBusinessUnits(data || []);
    });
  };

  const loadActivePackages = () => {
    callApiAsync(() => getPackage(true), (data) => setActivePackages(data || []));
  };

  const mapToPeriodOptions = (list) =>
    (list || []).map((p) => ({
      value: String(p.value ?? p.Value ?? ""),
      label: p.label ?? p.Label ?? ""
    }));

  const loadPeriodsForBusinessUnit = useCallback(
    (businessUnitId, afterLoad) => {
      if (!businessUnitId) return;
      const seq = ++periodsFetchSeqRef.current;
      setPeriodsLoadError(null);
      callApiAsync(
        () => getBillingPeriodsByBusinessUnit(businessUnitId),
        (list) => {
          if (seq !== periodsFetchSeqRef.current) return;
          setPeriodOptions((prev) => {
            const mapped = mapToPeriodOptions(list);
            const selectedId =
              String(getValues("idBillingPeriod") ?? "").trim() ||
              String(pendingSelectBillingPeriodRef.current ?? "").trim();
            if (!selectedId) return mapped;
            if (mapped.some((o) => String(o.value) === selectedId)) return mapped;
            const keep = prev.find((o) => String(o.value) === selectedId);
            if (keep) return [keep, ...mapped];
            return [
              { value: selectedId, label: `Periodo #${selectedId}` },
              ...mapped
            ];
          });
          if (afterLoad) queueMicrotask(() => afterLoad());
        },
        (error) => {
          if (seq !== periodsFetchSeqRef.current) return;
          pendingSelectBillingPeriodRef.current = null;
          const msg =
            typeof error === "string"
              ? error
              : error?.error ??
                error?.message ??
                "No se pudieron cargar los periodos.";
          setPeriodsLoadError(msg);
          setPeriodOptions([]);
        },
        setPeriodsLoading
      );
    },
    [getValues]
  );

  const loadPeriodInstances = useCallback(() => {
    if (!watchBusinessUnit || watchBillingPeriod === "" || watchBillingPeriod == null) {
      setPeriodInstances([]);
      return;
    }
    const pid = String(watchBillingPeriod);
    if (!periodOptions.some((o) => String(o.value) === pid)) {
      setPeriodInstances([]);
      return;
    }
    callApiAsync(
      () => getPricingInstance(watchBillingPeriod, watchBusinessUnit),
      (data) => setPeriodInstances(data || [])
    );
  }, [watchBillingPeriod, watchBusinessUnit, periodOptions]);

  useEffect(() => {
    loadBillingUnits();
    loadBusinessUnits();
    loadActivePackages();
  }, []);

  // Igual que Gestión de paquetes: filtrar businessUnits cuando cambia billingUnit
  useEffect(() => {
    if (watchBillingUnit) {
      const billingUnitKey = String(watchBillingUnit);
      const filtered = businessUnits.filter(
        (bu) => String(bu.idBillingUnit) === billingUnitKey
      );
      const selectData = transformToSelectData(filtered);
      setFilteredBusinessUnits(selectData);
      setValue("idBusinessUnit", "");
      setValue("idBillingPeriod", "");
      setPeriodOptions([]);
      setFilteredPackages([]);
    } else {
      setFilteredBusinessUnits([]);
      setValue("idBusinessUnit", "");
      setValue("idBillingPeriod", "");
      setPeriodOptions([]);
      setFilteredPackages([]);
    }
  }, [watchBillingUnit, businessUnits, setValue]);

  // Al cambiar businessUnit: cargar periodos de esa unidad y limpiar periodo seleccionado
  useEffect(() => {
    if (watchBusinessUnit) {
      pendingSelectBillingPeriodRef.current = null;
      setValue("idBillingPeriod", "");
      loadPeriodsForBusinessUnit(watchBusinessUnit);
    } else {
      setValue("idBillingPeriod", "");
      setPeriodOptions([]);
      setPeriodsLoadError(null);
      setPeriodsLoading(false);
      pendingSelectBillingPeriodRef.current = null;
    }
  }, [watchBusinessUnit, setValue, loadPeriodsForBusinessUnit]);

  // Filtrar paquetes activos por unidad de negocio (igual que Gestión de paquetes)
  useEffect(() => {
    if (watchBusinessUnit) {
      const businessUnitKey = String(watchBusinessUnit);
      const filtered = activePackages.filter(
        (pkg) => String(pkg.idBusinessUnit) === businessUnitKey
      );
      setFilteredPackages(filtered);
    } else {
      setFilteredPackages([]);
    }
  }, [watchBusinessUnit, activePackages]);

  useEffect(() => {
    loadPeriodInstances();
  }, [loadPeriodInstances]);

  const handleAddToBill = (row) => {
    setEditingInstanceId(null);
    setSelectedPackage(row);
    setOpenAddModal(true);
  };

  const handleCloseModal = () => {
    setOpenAddModal(false);
    setSelectedPackage(null);
    setEditingInstanceId(null);
  };

  const handleCreated = () => {
    setToastSeverity("success");
    setToastMessage("Paquete agregado a facturación correctamente.");
    setToastOpen(true);
    handleCloseModal();
    loadPeriodInstances();
  };

  const handleCloseBillingConfirm = () => {
    if (!watchBillingPeriod) return;
    setClosingBilling(true);
    callApiAsync(
      () => closeBillingAndCreateNextPeriod(watchBillingPeriod),
      (data) => {
        setCloseBillingAlertOpen(false);
        setClosingBilling(false);
        setToastSeverity("success");
        setToastMessage(data?.message ?? "Facturación cerrada y siguiente periodo creado.");
        setToastOpen(true);
        loadPeriodsForBusinessUnit(watchBusinessUnit, () => {
          if (data?.newPeriodId != null) {
            setValue("idBillingPeriod", String(data.newPeriodId), {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            });
          }
        });
        loadPeriodInstances();
      },
      (err) => {
        setClosingBilling(false);
        setToastSeverity("error");
        setToastMessage(err?.message ?? err?.error ?? err?.detail ?? "Error al cerrar facturación.");
        setToastOpen(true);
      }
    );
  };

  const handleInstanceUpdated = () => {
    loadPeriodInstances();
  };

  const handleEditInstance = (row) => {
    setSelectedPackage(null);
    setEditingInstanceId(row.id ?? row.Id);
    setOpenAddModal(true);
  };

  const handleDeleteInstanceClick = (row) => {
    setInstanceToDelete(row);
    setDeleteInstanceAlertOpen(true);
  };

  const handleDeleteInstanceConfirm = () => {
    if (!instanceToDelete) return;
    const id = instanceToDelete.id ?? instanceToDelete.Id;
    callApiAsync(
      () => deletePricingInstance(id),
      () => {
        setDeleteInstanceAlertOpen(false);
        setInstanceToDelete(null);
        setToastSeverity("success");
        setToastMessage("Elemento de facturación eliminado correctamente.");
        setToastOpen(true);
        loadPeriodInstances();
      },
      (err) => {
        setToastSeverity("error");
        setToastMessage(err?.message ?? err?.error ?? err?.detail ?? "Error al eliminar.");
        setToastOpen(true);
      }
    );
  };

  const handleConfirmInitialPeriod = () => {
    const buId = Number(watchBusinessUnit);
    if (!buId) return;
    const { year, month } = getInitialPeriodTargetUtc(initialPeriodChoice);

    callApiAsync(
      () =>
        createInitialBillingPeriod({
          businessUnitId: buId,
          year,
          month
        }),
      (res) => {
        const newId = res?.newPeriodId ?? res?.NewPeriodId;
        const y = res?.year ?? res?.Year ?? year;
        const m = res?.month ?? res?.Month ?? month;
        if (newId == null) {
          setToastSeverity("error");
          setToastMessage("Respuesta del servidor sin id de periodo.");
          setToastOpen(true);
          return;
        }
        const v = String(newId);
        const label = `${MONTH_NAMES_ES[m - 1]} - ${y}`;
        pendingSelectBillingPeriodRef.current = v;
        setPeriodOptions((prev) => {
          if (prev.some((o) => String(o.value) === v)) return prev;
          return [{ value: v, label }, ...prev];
        });
        setValue("idBillingPeriod", v, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        loadPeriodsForBusinessUnit(watchBusinessUnit, () => {
          const pending = pendingSelectBillingPeriodRef.current;
          if (pending) {
            setValue("idBillingPeriod", pending, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            });
          }
          pendingSelectBillingPeriodRef.current = null;
        });
        setOpenInitialPeriodModal(false);
        setToastSeverity("success");
        setToastMessage(res?.message ?? res?.Message ?? "Periodo creado correctamente.");
        setToastOpen(true);
      },
      (error) => {
        const msg =
          typeof error === "string"
            ? error
            : error?.error ??
              error?.message ??
              error?.detail ??
              "Error al crear el periodo.";
        setToastSeverity("error");
        setToastMessage(msg);
        setToastOpen(true);
      },
      setCreatingInitialPeriod
    );
  };

  const actionBtn = ({ row }) => (
    <div className="HContainer">
      <ButtonComponent
        onClick={() => handleAddToBill(row.original)}
        variant="contained"
        backgroundColor="#fff0"
        title="Agregar a facturar"
      >
        <img className="btn-icon w-icon-15" src={icons.plus} alt="Agregar a facturar" />
      </ButtonComponent>
    </div>
  );

  const instanceActionBtn = ({ row }) => {
    const isLastPeriod = row.original.isLastPeriod ?? row.original.IsLastPeriod;
    return (
      <div className="HContainer" style={{ gap: 4 }}>
        {isLastPeriod && (
          <>
            <ButtonComponent
              onClick={() => handleEditInstance(row.original)}
              variant="contained"
              backgroundColor="#fff0"
              title="Editar usuarios y features"
            >
              <img
                className="btn-icon w-icon-15"
                src={icons.blue_edit}
                alt="Editar elemento"
              />
            </ButtonComponent>
            <ButtonComponent
              onClick={() => handleDeleteInstanceClick(row.original)}
              variant="contained"
              backgroundColor="#fff0"
              title="Eliminar"
            >
              <img
                className="btn-icon w-icon-15"
                src={icons.red_delete}
                alt="Eliminar"
              />
            </ButtonComponent>
          </>
        )}
      </div>
    );
  };

  const getVal = (obj, a, b) => obj[a] ?? obj[b] ?? 0;

  const getRowBase = (row) => {
    const packagePrice = Number(getVal(row, "packagePrice", "PackagePrice")) || 0;
    const individualPrice = Number(getVal(row, "individualPrice", "IndividualPrice")) || 0;
    const userCount = Number(getVal(row, "selectedUsersCount", "SelectedUsersCount")) || 0;
    return packagePrice > 0 ? packagePrice : individualPrice * userCount;
  };

  const getTaxPercent = (row) => Number(getVal(row, "tax", "Tax")) || 0;

  const getTaxValue = (row) => {
    const base = getRowBase(row);
    const taxPercent = getTaxPercent(row);
    return base * (taxPercent / 100);
  };

  const getRowTotal = (row) => {
    const base = getRowBase(row);
    const taxValue = getTaxValue(row);
    return base + taxValue;
  };

  const getRowSiglas = (row) => (row.siglas ?? row.Siglas ?? "").toString().toUpperCase();
  const isRowCOP = (row) => getRowSiglas(row) === "COP";
  const isRowUSD = (row) => getRowSiglas(row) === "USD";

  const instanceColumns = useMemo(
    () => [
      textColumn("name", "Paquete", 180),
      textColumn("mainProductName", "Producto principal", 160),
      { id: "individualPrice", header: "Precio individual", size: 120, Cell: ({ row }) => `$${Number(getVal(row.original, "individualPrice", "IndividualPrice")).toLocaleString()}` },
      { id: "packageSize", header: "Tamaño paquete", size: 100, Cell: ({ row }) => getVal(row.original, "packageSize", "PackageSize") },
      { id: "packagePrice", header: "Precio paquete", size: 120, Cell: ({ row }) => `$${Number(getVal(row.original, "packagePrice", "PackagePrice")).toLocaleString()}` },
      { id: "maxMembersToCharge", header: "Máx. miembros", size: 100, Cell: ({ row }) => getVal(row.original, "maxMembersToCharge", "MaxMembersToCharge") },
      {
        id: "selectedUsersCount",
        header: "Cantidad de usuarios",
        size: 110,
        Cell: ({ row }) => getVal(row.original, "selectedUsersCount", "SelectedUsersCount"),
        Footer: ({ table }) => {
          const total = table.getRowModel().rows.reduce(
            (acc, r) => acc + Number(getVal(r.original, "selectedUsersCount", "SelectedUsersCount")) || 0,
            0
          );
          return `Total usuarios: ${total.toLocaleString()}`;
        }
      },
      { id: "tax", header: "Impuesto (%)", size: 100, Cell: ({ row }) => `${Number(getTaxPercent(row.original)).toLocaleString()}%` },
      { id: "taxValue", header: "Valor del impuesto", size: 130, Cell: ({ row }) => `$${Math.round(getTaxValue(row.original)).toLocaleString()}` },
      {
        id: "totalCOP",
        header: "Total (COP)",
        size: 120,
        Cell: ({ row }) => (isRowCOP(row.original) ? `$${getRowTotal(row.original).toLocaleString()}` : "-"),
        Footer: ({ table }) => {
          const sum = table.getRowModel().rows
            .filter((r) => isRowCOP(r.original))
            .reduce((acc, r) => acc + getRowTotal(r.original), 0);
          return `Total COP: $${sum.toLocaleString()}`;
        }
      },
      {
        id: "totalUSD",
        header: "Total (USD)",
        size: 120,
        Cell: ({ row }) => (isRowUSD(row.original) ? `$${getRowTotal(row.original).toLocaleString()}` : "-"),
        Footer: ({ table }) => {
          const sum = table.getRowModel().rows
            .filter((r) => isRowUSD(r.original))
            .reduce((acc, r) => acc + getRowTotal(r.original), 0);
          return `Total USD: $${sum.toLocaleString()}`;
        }
      },
      { id: "siglas", header: "Moneda", size: 80, Cell: ({ row }) => row.original.siglas ?? row.original.Siglas ?? "" },
      actionsColumn(instanceActionBtn, 100)
    ],
    []
  );

  const periodIdSelected =
    watchBillingPeriod === "" || watchBillingPeriod == null
      ? ""
      : String(watchBillingPeriod);
  const periodSelectionValid =
    periodIdSelected !== "" &&
    periodOptions.some((o) => String(o.value) === periodIdSelected);

  const filtersReady =
    watchBillingUnit && watchBusinessUnit && periodSelectionValid;

  const showInitialPeriodCta =
    watchBillingUnit &&
    watchBusinessUnit &&
    !periodsLoading &&
    !periodsLoadError &&
    periodOptions.length === 0;

  const initialTarget = getInitialPeriodTargetUtc(initialPeriodChoice);
  const initialTargetLabel = `${MONTH_NAMES_ES[initialTarget.month - 1]} ${initialTarget.year}`;

  // El primer periodo en la lista es el más reciente (último/abierto). Si el seleccionado no es ese, el periodo está cerrado.
  const selectedPeriodValue = periodIdSelected;
  const firstPeriodValue =
    periodOptions.length > 0 ? String(periodOptions[0].value) : "";
  const isLastPeriod =
    selectedPeriodValue !== "" &&
    firstPeriodValue !== "" &&
    selectedPeriodValue === firstPeriodValue;

  const packageColumnsWithActions = useMemo(
    () => [
      textColumn("name", "Nombre del paquete", 200),
      textColumn("mainProductName", "Producto principal", 160),
      tooltipColumn("description", "Descripción", 250),
      actionsColumn(actionBtn, 80)
    ],
    []
  );

  const packageColumnsWithoutActions = useMemo(
    () => [
      textColumn("name", "Nombre del paquete", 200),
      textColumn("mainProductName", "Producto principal", 160),
      tooltipColumn("description", "Descripción", 250)
    ],
    []
  );

  const packageColumns = isLastPeriod ? packageColumnsWithActions : packageColumnsWithoutActions;

  return (
    <div>
      <h1>Facturación (PricingInstance)</h1>

      <Grid container spacing={2} className="mb-20">
        <Grid item xs={12} md={4}>
          <SearchableSelectInput
            name="idBillingUnit"
            control={control}
            label="Unidad de Facturación"
            options={billingUnits}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SearchableSelectInput
            name="idBusinessUnit"
            control={control}
            label="Unidad de Negocio"
            options={filteredBusinessUnits}
            disabled={!watchBillingUnit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SearchableSelectInput
            name="idBillingPeriod"
            control={control}
            label="Periodo"
            options={periodOptions}
            disabled={!watchBusinessUnit}
          />
        </Grid>
        {filtersReady && isLastPeriod && (
          <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}>
            <ButtonComponent
              onClick={() => setCloseBillingAlertOpen(true)}
              backgroundColor="#2E7D32"
              textColor="#fff"
              disable={closingBilling}
            >
              Cerrar facturación y crear siguiente periodo
            </ButtonComponent>
          </Grid>
        )}
      </Grid>

      {watchBillingUnit && watchBusinessUnit && (
        <div style={{ marginTop: 12 }}>
          {periodsLoading && (
            <p style={{ color: "#666" }}>Cargando periodos…</p>
          )}
          {periodsLoadError && (
            <div
              className="HContainer"
              style={{ gap: 16, alignItems: "center", flexWrap: "wrap" }}
            >
              <p style={{ color: "#c62828", margin: 0 }}>{periodsLoadError}</p>
              <ButtonComponent
                onClick={() => loadPeriodsForBusinessUnit(watchBusinessUnit)}
                variant="contained"
                backgroundColor="#1976d2"
                textColor="#fff"
              >
                Reintentar
              </ButtonComponent>
            </div>
          )}
          {showInitialPeriodCta && (
            <div
              style={{
                marginTop: 8,
                padding: 16,
                background: "#fff8e1",
                borderRadius: 8,
                border: "1px solid #ffe082"
              }}
            >
              <p style={{ margin: "0 0 12px 0" }}>
                No hay periodos de facturación para esta unidad. Cree el primero
                (mes actual o anterior en UTC).
              </p>
              <ButtonComponent
                onClick={() => setOpenInitialPeriodModal(true)}
                backgroundColor="#F6C079"
                textColor="#000000"
              >
                Crear periodo inicial
              </ButtonComponent>
            </div>
          )}
        </div>
      )}

      {!filtersReady && (
        <div className="HContainer j-center mt-40">
          <p style={{ color: "#666", fontSize: "16px" }}>
            Seleccione Unidad de Facturación, Unidad de Negocio y Periodo para
            ver los paquetes activos y agregar a facturar.
          </p>
        </div>
      )}

      {filtersReady && (
        <>
          <h2 style={{ marginTop: 24, marginBottom: 12 }}>
            Paquetes activos – Agregar a facturar
          </h2>
          <TableComponent
            columns={packageColumns}
            data={filteredPackages}
            enableGlobalFilter={true}
            initialState={{ sorting: [{ id: "name", desc: false }] }}
          />

          <h2 style={{ marginTop: 32, marginBottom: 12 }}>
            Elementos de facturación en este periodo
          </h2>
          <TableComponent
            columns={instanceColumns}
            data={periodInstances}
            enableGlobalFilter={true}
            initialState={{ sorting: [{ id: "name", desc: false }] }}
          />
        </>
      )}

      <AddPackageToBillModal
        open={openAddModal}
        onClose={handleCloseModal}
        packageRow={selectedPackage}
        idBillingPeriod={watchBillingPeriod}
        pricingInstanceId={editingInstanceId}
        onSuccess={handleCreated}
        onError={(msg) => {
          setToastSeverity("error");
          setToastMessage(msg);
          setToastOpen(true);
        }}
      />

      <AlertComponent
        open={closeBillingAlertOpen}
        onClose={() => !closingBilling && setCloseBillingAlertOpen(false)}
        onConfirm={handleCloseBillingConfirm}
        title="Cerrar facturación y crear siguiente periodo"
        message="Se creará el siguiente periodo (mes siguiente) y se copiarán todos los principales, las adiciones temporales no vencidas (sus usuarios se agregarán al principal) y las remociones temporales no vencidas. ¿Continuar?"
        severity="danger"
      />

      <AlertComponent
        open={deleteInstanceAlertOpen}
        onClose={() => setDeleteInstanceAlertOpen(false)}
        onConfirm={handleDeleteInstanceConfirm}
        title="Eliminar elemento de facturación"
        message="¿Está seguro de eliminar este elemento? Si es una remoción temporal, los usuarios se devolverán al paquete principal."
        severity="danger"
      />

      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />

      <ModalComponent
        open={openInitialPeriodModal}
        onClose={() => !creatingInitialPeriod && setOpenInitialPeriodModal(false)}
        title="Crear periodo inicial"
      >
        <p style={{ marginBottom: 12 }}>
          Se creará el periodo <strong>{initialTargetLabel}</strong>.
        </p>
        <RadioGroup
          value={initialPeriodChoice}
          onChange={(e) => setInitialPeriodChoice(e.target.value)}
        >
          <FormControlLabel
            value="current"
            control={<Radio color="primary" />}
            label="Mes calendario actual"
          />
          <FormControlLabel
            value="previous"
            control={<Radio color="primary" />}
            label="Mes anterior — facturación mes vencido"
          />
        </RadioGroup>
        <div className="HContainer j-center mt-20" style={{ gap: 12 }}>
          <ButtonComponent
            onClick={() => setOpenInitialPeriodModal(false)}
            variant="contained"
            backgroundColor="#e0e0e0"
            textColor="#000"
            disable={creatingInitialPeriod}
          >
            Cancelar
          </ButtonComponent>
          <ButtonComponent
            onClick={handleConfirmInitialPeriod}
            variant="contained"
            backgroundColor="#F6C079"
            textColor="#000000"
            disable={creatingInitialPeriod}
          >
            {creatingInitialPeriod ? "Creando…" : "Crear periodo"}
          </ButtonComponent>
        </div>
      </ModalComponent>
    </div>
  );
};

export default PricingInstanceFacturacion;
